import { AddressLink, PtoV2DeployParams } from 'Modules'
import { parseEventLogs, zeroAddress } from 'viem'

import abiPTOFactoryV2 from 'abi/abi-pto-factory-v2.json'
import { SettingEnum } from 'constants/setting'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { useAppSetting } from 'hooks/setting/use-app-setting'
import { useContractManager } from 'hooks/use-contract-manager'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

/**
 * PTO v2 - deploy collection via PTOFactoryUpgradeable.deployCollection(DeployParams).
 * Pool/phases/rarities are no longer part of the deployment (moved to createPool).
 */
export const useDeployCollection = () => {
  const { getSetting } = useAppSetting()

  const ptoFactoryV2Contract = getSetting(SettingEnum.PTO_FACTORY_V2_CONTRACT)
  const ptoDistributionContract = getSetting(SettingEnum.PTO_DISTRIBUTION_CONTRACT)

  const contract = useContractViem(ptoFactoryV2Contract, abiPTOFactoryV2)

  console.log('ptoFactoryV2Contract', ptoFactoryV2Contract)

  const { isContractManager } = useContractManager({ contract })

  const initDeployParams = (deployer: AddressLink, input?: any): PtoV2DeployParams | null => {
    if (!input) return null

    const initDistribution = input.initDistribution || ptoDistributionContract
    if (!initDistribution) return null

    return {
      externalId: input.externalId,
      name: input.contractName || input.name || input.ptoName,
      symbol: input.symbol,
      initBaseURI: input.initBaseURI,
      initDistribution,
      // managerAddress is the currently connected admin wallet
      managerAddress: deployer,
      ownerAddress: input.ownerAddress,
      maxNFTSupply: input.maxNFTSupply ?? input.pool?.maxNFTSupply,
      // BE already returns royaltyFee in basis points (e.g. 250 = 2.5%)
      royaltyFee: Number(input.royaltyFee || 0),
      whitelistSigner: input.whitelistSigner,
      // BE returns stakingNFTAddress for Rental PTO; regular PTO uses zero address
      operatorWhitelistAddress: input.stakingNFTAddress || zeroAddress,
    }
  }

  const deployCollection = async (address: AddressLink, input: any) => {
    if (!ptoFactoryV2Contract || !contract) {
      throw new Error('PTO Factory v2 contract is not configured')
    }
    if (!address || !input) return null

    const params = initDeployParams(address, input)
    if (!params) {
      throw new Error('Missing deploy collection params')
    }

    try {
      const { request } = await contract.simulate.deployCollection([params], {
        account: address,
      })
      const txHash = await clientWallet?.writeContract(request)

      const receipt = await publicWallet.waitForTransactionReceipt({
        hash: txHash,
        confirmations: 1,
      })
      const logs: any = parseEventLogs({
        abi: abiPTOFactoryV2,
        logs: receipt.logs,
        eventName: 'CollectionDeployed',
      })

      if (logs.length > 0) {
        return logs[0].args.collectionAddress as AddressLink
      }
      return null
    } catch (error) {
      logError('error deploy collection v2-----------------', error)
      throw error
    }
  }

  return {
    isContractManager,
    deployCollection,
  }
}
